import { useState, useEffect, useMemo } from 'react';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SG7TacticalPanel } from "./sg7";
import { FileText, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useTranslation } from "react-i18next";

interface ArticleRendererProps {
  html?: string | null;
  markdown?: string | null;
  className?: string;
  referee?: string | null;
}

// ============================================================================
// REFEREE: Replace placeholder text with actual referee name
// ============================================================================
const REFEREE_PLACEHOLDER_REGEX = /[*-]*\s*\*{0,2}(?:Referee|Árbitro|Arbitro)\*{0,2}\s*:?\s*\*{0,2}\s*\(?[^)\n]*(?:not available|unavailable|TBA|TBD|not yet|N\/A|a definir|por anunciar|a anunciar|por designar|não|unknown|DATA_CLIPS|information|—|-)[^)\n]*\)?/gi;

// Matches the full line containing a referee placeholder (including surrounding whitespace/newlines)
const REFEREE_LINE_REGEX = /[ \t]*[*-]*\s*\*{0,2}(?:Referee|Árbitro|Arbitro)\*{0,2}\s*:?\s*\*{0,2}\s*\(?[^)\n]*(?:not available|unavailable|TBA|TBD|not yet|N\/A|a definir|por anunciar|a anunciar|por designar|não|unknown|DATA_CLIPS|information|—|-)[^)\n]*\)?\s*\n?/gi;

function injectReferee(content: string, referee: string | null | undefined): string {
  if (!content) return content;

  // No referee data → strip placeholder lines entirely to avoid noise
  if (!referee) {
    return content.replace(REFEREE_LINE_REGEX, '').replace(/\n{3,}/g, '\n\n');
  }

  const refereeName = referee.includes(',') ? referee.split(',')[0].trim() : referee.trim();
  if (!refereeName) {
    return content.replace(REFEREE_LINE_REGEX, '').replace(/\n{3,}/g, '\n\n');
  }

  return content.replace(REFEREE_PLACEHOLDER_REGEX, (match) => {
    const hasBold = match.includes('**');
    const stripped = match.replace(/\*/g, '').toLowerCase();
    const label = stripped.startsWith('árbitro') || stripped.startsWith('arbitro')
      ? 'Árbitro' : 'Referee';
    return hasBold ? `**${label}:** ${refereeName}` : `${label}: ${refereeName}`;
  });
}

interface Section {
  title: string;
  content: string;
}

// ============================================================================
// SECURITY: Safe URL validation
// ============================================================================
const SAFE_URL_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

function isSafeUrl(href: string | undefined): boolean {
  if (!href) return false;
  try {
    const url = new URL(href, 'https://example.com');
    return SAFE_URL_PROTOCOLS.includes(url.protocol);
  } catch {
    // Relative URLs are OK, but block known dangerous schemes
    const lower = href.toLowerCase().trim();
    return !lower.startsWith('javascript:') &&
           !lower.startsWith('data:') &&
           !lower.startsWith('vbscript:');
  }
}

// ============================================================================
// SECURITY: Configure DOMPurify once at module level
// ============================================================================
let domPurifyConfigured = false;

function configureDOMPurify() {
  if (domPurifyConfigured) return;
  
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
      
      // Validate href
      const href = node.getAttribute('href') || '';
      if (!isSafeUrl(href)) {
        node.removeAttribute('href');
      }
    }
  });
  
  domPurifyConfigured = true;
}

// Initialize DOMPurify config on module load
configureDOMPurify();

// ============================================================================
// PARSING: Markdown sections by ## headings
// ============================================================================
function parseMarkdownSections(md: string): Section[] {
  // Strip leading h1 line (AI-generated title like "# Professional Analysis: ...")
  const stripped = md.replace(/^#\s+.+\n*/m, '');
  const lines = stripped.split('\n');
  const sections: Section[] = [];
  let currentTitle = '';
  let buffer: string[] = [];
  
  for (const line of lines) {
    if (line.startsWith('## ')) {
      // Push previous section (including intro or headings without content)
      if (buffer.length > 0 || currentTitle) {
        sections.push({ 
          title: currentTitle, 
          content: buffer.join('\n').trim() 
        });
      }
      currentTitle = line.replace(/^## /, '').trim();
      buffer = [];
    } else {
      buffer.push(line);
    }
  }
  
  // Push last section
  if (buffer.length > 0 || currentTitle) {
    sections.push({ 
      title: currentTitle, 
      content: buffer.join('\n').trim() 
    });
  }
  
  // If no sections found, return entire content as single section
  return sections.length > 0 ? sections : [{ title: '', content: md.trim() }];
}

// ============================================================================
// PARSING: HTML sections by <h2> tags (legacy fallback)
// ============================================================================
function parseHtmlSections(htmlContent: string): Section[] {
  // Strip leading h1 tag (AI-generated title like "<h1>Professional Analysis: ...</h1>")
  const cleaned = htmlContent.replace(/^\s*<h1[^>]*>.*?<\/h1>\s*/i, '');
  const sections: Section[] = [];
  
  // Split by H2 tags while keeping the delimiter
  const h2Pattern = /<h2[^>]*>(.*?)<\/h2>/gi;
  const parts = cleaned.split(h2Pattern);
  
  if (parts.length <= 1) {
    // No H2 found, return entire content as single section
    return [{ title: '', content: htmlContent }];
  }
  
  // First part is content before any H2 (if exists)
  if (parts[0].trim()) {
    sections.push({ title: '', content: parts[0] });
  }
  
  // Process pairs: [title, content, title, content, ...]
  for (let i = 1; i < parts.length; i += 2) {
    const title = parts[i]?.replace(/<[^>]*>/g, '').trim() || '';
    const content = parts[i + 1] || '';
    // Preserve headings even without content
    if (title || content.trim()) {
      sections.push({ title, content });
    }
  }
  
  return sections;
}

// ============================================================================
// COMPONENTS: Custom Markdown renderers
// ============================================================================
const MarkdownComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
    if (!isSafeUrl(href)) {
      return <span>{children}</span>;
    }
    return (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-[hsl(var(--sg7-gold))] hover:underline"
      >
        {children}
      </a>
    );
  },
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="text-[hsl(var(--sg7-gold))] bg-[hsl(var(--sg7-surface-3))] px-1.5 py-0.5 rounded text-sm font-semibold">
      {children}
    </code>
  ),
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export function ArticleRenderer({ html, markdown, className, referee }: ArticleRendererProps) {
  const { t } = useTranslation('analysis');
  
  // MD-first: prioritize markdown, fallback to html
  const useMarkdown = Boolean(markdown && markdown.trim());
  const useHtml = !useMarkdown && Boolean(html && html.trim());
  
  if (!useMarkdown && !useHtml) return null;

  // Apply referee injection to content
  const processedMarkdown = useMemo(() => {
    if (!useMarkdown || !markdown) return null;
    return injectReferee(markdown, referee);
  }, [useMarkdown, markdown, referee]);

  const processedHtml = useMemo(() => {
    if (!useHtml || !html) return null;
    return injectReferee(html, referee);
  }, [useHtml, html, referee]);

  // Sanitize HTML (only if using HTML fallback)
  const sanitizedHtml = useMemo(() => {
    if (!useHtml || !processedHtml) return null;
    
    return DOMPurify.sanitize(processedHtml, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'blockquote', 'code', 'pre',
        'a', 'span', 'div',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'hr'
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'] // NO style
    });
  }, [useHtml, processedHtml]);

  // Parse sections (memoized)
  const mdSections = useMemo(() => {
    if (!useMarkdown || !processedMarkdown) return [];
    return parseMarkdownSections(processedMarkdown);
  }, [useMarkdown, processedMarkdown]);

  const htmlSections = useMemo(() => {
    if (!useHtml || !sanitizedHtml) return [];
    return parseHtmlSections(sanitizedHtml);
  }, [useHtml, sanitizedHtml]);

  // Combined sections
  const sections = useMarkdown ? mdSections : htmlSections;

  // Content key for detecting article changes
  const contentKey = useMemo(() => {
    return markdown?.slice(0, 100) || html?.slice(0, 100) || '';
  }, [markdown, html]);

  // Track open sections - reset when article changes
  const [openSections, setOpenSections] = useState<Set<number>>(() => new Set([0]));

  useEffect(() => {
    const firstTitledIndex = sections.findIndex(s => s.title);
    setOpenSections(new Set([firstTitledIndex >= 0 ? firstTitledIndex : 0]));
  }, [contentKey]);

  const toggleSection = (index: number) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Prose styles for dark reader panel
  const proseClasses = `
    prose prose-base max-w-none
    prose-headings:font-bold prose-headings:first:mt-0
    prose-headings:text-[hsl(var(--sg7-text))]
    prose-h2:text-[hsl(var(--sg7-gold))] prose-h2:text-lg prose-h2:font-black
    prose-h2:border-b prose-h2:border-[hsl(var(--sg7-stroke-subtle))] prose-h2:pb-2
    prose-h2:mt-6 prose-h2:mb-4
    prose-h3:text-[hsl(var(--sg7-gold)/0.8)] prose-h3:text-base prose-h3:font-bold
    prose-h3:mt-5 prose-h3:mb-3
    prose-h4:text-[hsl(var(--sg7-gold)/0.7)] prose-h4:text-sm prose-h4:font-semibold
    prose-h4:mt-4 prose-h4:mb-2
    prose-p:text-[hsl(var(--sg7-text)/0.92)] prose-p:text-sm prose-p:leading-[1.8] prose-p:my-4
    prose-strong:text-[hsl(var(--sg7-text))] prose-strong:font-semibold
    prose-ul:text-[hsl(var(--sg7-text)/0.92)] prose-ul:my-4 prose-ul:pl-5
    prose-ol:text-[hsl(var(--sg7-text)/0.92)] prose-ol:my-4 prose-ol:pl-5
    prose-li:text-[hsl(var(--sg7-text)/0.92)] prose-li:text-sm prose-li:my-2 prose-li:leading-[1.8]
    prose-a:text-[hsl(var(--sg7-gold))] prose-a:no-underline hover:prose-a:underline
    prose-blockquote:border-l-[hsl(var(--sg7-gold))] prose-blockquote:text-[hsl(var(--sg7-text-muted))] prose-blockquote:my-5 prose-blockquote:pl-4 prose-blockquote:italic
    prose-code:text-[hsl(var(--sg7-gold))] prose-code:bg-[hsl(var(--sg7-surface-3))] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-semibold prose-code:before:content-[''] prose-code:after:content-['']
    prose-table:my-4 prose-table:text-sm
    prose-th:bg-[hsl(var(--sg7-surface-3))] prose-th:p-2 prose-th:text-left prose-th:font-semibold prose-th:text-[hsl(var(--sg7-text))]
    prose-td:p-2 prose-td:border-b prose-td:border-[hsl(var(--sg7-stroke-subtle))] prose-td:text-[hsl(var(--sg7-text)/0.92)]
    prose-hr:my-6 prose-hr:border-[hsl(var(--sg7-stroke-subtle))]
  `;

  // Check if we have multiple titled sections for accordion mode
  const hasMultipleSections = sections.filter(s => s.title).length > 1;

  // Render section content based on source type
  const renderSectionContent = (section: Section) => {
    if (useMarkdown) {
      return (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={MarkdownComponents}
          className={proseClasses}
        >
          {section.content}
        </ReactMarkdown>
      );
    } else {
      return (
        <div 
          className={proseClasses}
          dangerouslySetInnerHTML={{ __html: section.content }}
        />
      );
    }
  };

  return (
    <SG7TacticalPanel className={className}>
      <div className="p-4 bg-[hsl(var(--sg7-surface-2))]">
        <h3 className="text-base font-bold flex items-center gap-2 text-[hsl(var(--sg7-text))] mb-4">
          <FileText className="h-5 w-5 text-[hsl(var(--sg7-gold))]" />
          {t('cards.detailedAnalysis')}
        </h3>
        
        {hasMultipleSections ? (
          // Accordion mode for multiple sections
          <div className="space-y-2">
            {sections.map((section, index) => (
              section.title ? (
                <Collapsible
                  key={index}
                  open={openSections.has(index)}
                  onOpenChange={() => toggleSection(index)}
                >
                  <CollapsibleTrigger className="w-full">
                    <div className={cn(
                      "flex items-center justify-between p-3 rounded-lg transition-colors",
                      "bg-[hsl(var(--sg7-surface-3))] hover:bg-[hsl(var(--sg7-surface-3)/0.8)]",
                      "border border-[hsl(var(--sg7-stroke-subtle))]"
                    )}>
                      <span className="text-sm font-bold text-[hsl(var(--sg7-gold))]">
                        {section.title}
                      </span>
                      {openSections.has(index) ? (
                        <ChevronDown className="h-4 w-4 text-[hsl(var(--sg7-text-muted))]" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-[hsl(var(--sg7-text-muted))]" />
                      )}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="pt-3 px-1">
                      {renderSectionContent(section)}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ) : (
                // Content without title (intro paragraph)
                <div key={index} className="mb-4">
                  {renderSectionContent(section)}
                </div>
              )
            ))}
          </div>
        ) : (
          // Single section mode - no accordion
          <div>
            {sections.map((section, index) => (
              <div key={index}>
                {renderSectionContent(section)}
              </div>
            ))}
          </div>
        )}
      </div>
    </SG7TacticalPanel>
  );
}
